import React, { useEffect, useRef, useState } from "react";

import styles from "./MoviesList.module.scss";
import MovieCard from "./MovieCard";
import VerticalMovieCard from "./VerticalMovieCard";
import Heading from "../../../components/shared/Heading";
import DotsLoader from "../../../components/icons/DotsLoader";
import { useTranslation } from "react-i18next";
import { setVoteCount } from "../../../store/slices/lists.slice";
import { useDispatch } from "react-redux";
import { setVoteCountFavourites } from "../../../store/slices/favouritesList.slice";
import useWindowSize from "../../../hooks/useWindowSize";
import ViewSwitcher, { SearchViewMode } from "./ViewSwitcher";
import EmptyState from "../../../components/shared/EmptyState";
import FilterHeaderHash from "../../../components/icons/forYou/FilterHeaderHash";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import FilterIcon from "../../../components/icons/Filters";

interface ISimilarContent {
  error: any;
  data: any[];
  setPageHandler: () => void;
  isFetching?: boolean;
  isLastData?: boolean;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  title: string;
  likesList: number[];
  favoritesList: number[];
  isFavoritePage: boolean;
  enableViewSwitcher?: boolean;
  viewStorageKey?: string;
  activeFilterLabels?: string[];
}

const DEFAULT_VIEW_MODE: SearchViewMode = "list";

const getSavedViewMode = (storageKey?: string): SearchViewMode => {
  if (!storageKey) return DEFAULT_VIEW_MODE;

  try {
    const savedViewMode = localStorage.getItem(storageKey);
    return savedViewMode === "grid" || savedViewMode === "list" ? savedViewMode : DEFAULT_VIEW_MODE;
  } catch (e) {
    return DEFAULT_VIEW_MODE;
  }
};

export interface Movie {
  id: number;
  genre_ids: number[];
  reviews_count: number | null;
  adult: boolean;
  backdrop_path: string;
  original_language: string;
  original_title: string;
  overview: string;
  overview_ru: string;
  overview_ua: string;
  popularity: number;
  poster_path: string;
  release_date: string;
  title: string;
  title_ru: string;
  title_ua: string;
  video: boolean;
  vote_average: number;
  vote_count: number;
  not_found: boolean;
  liked: boolean;
  in_favorite: boolean;
  imdb_rating?: number | null;
  imdb_votes?: number | null;
  kinopoisk_rating?: number | null;
  kinopoisk_votes?: number | null;
  rotten_tomatoes_rating?: number | null;
  rotten_tomatoes_audience_rating?: number | null;
}

const MoviesList = ({
  data,
  setPageHandler,
  isFetching,
  error,
  isLastData,
  likeHandler,
  favouritesHandler,
  title,
  likesList,
  favoritesList,
  isFavoritePage,
  enableViewSwitcher = false,
  viewStorageKey,
  activeFilterLabels = [],
}: ISimilarContent) => {
  const paginationAnchorRef = useRef(null);
  const [activeView, setActiveView] = useState<SearchViewMode>(() => getSavedViewMode(viewStorageKey));
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { windowSize } = useWindowSize();
  const isMobile = windowSize === "mobile";
  const viewMode = enableViewSwitcher ? activeView : "list";

  useEffect(() => {
    setActiveView(getSavedViewMode(viewStorageKey));
  }, [viewStorageKey]);

  const setViewModeHandler = (nextViewMode: SearchViewMode) => {
    setActiveView(nextViewMode);

    if (!viewStorageKey) return;

    try {
      localStorage.setItem(viewStorageKey, nextViewMode);
    } catch (e) {
      return;
    }
  };

  useEffect(() => {
    if (isFetching) return;

    const elem = paginationAnchorRef.current;
    const handleIntersection = (entries: any) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting && data.length && !error) {
          setPageHandler();
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 0.1,
    });

    if (elem) {
      observer.observe(elem);
    }

    return () => {
      if (elem) {
        observer.unobserve(elem);
      }
    };
  }, [setPageHandler, isFetching, data?.length, error]);

  const updateVoteCount = (id: any, key: any, type: any) => {
    if (isFavoritePage) {
      dispatch(setVoteCountFavourites({ id, key, type }));
    } else {
      dispatch(setVoteCount({ id, key, type }));
    }
  };

  const emptyPageContent = () => {
    return (
      <EmptyState
        className={styles.emptyWrapper}
        variant={isFavoritePage ? "favorites" : "search"}
        text={t(isFavoritePage ? "emptyFavourites" : "emptySearch")}
      />
    );
  };

  const renderSearchFilters = () => {
    const labels = activeFilterLabels.length ? activeFilterLabels : [t("all")];

    return (
      <div
        className={`${styles.activeFiltersWrapper} ${isMobile ? styles.activeFiltersWrapperMobile : ""}`}
        role={isMobile ? "button" : undefined}
        tabIndex={isMobile ? 0 : undefined}
        onClick={isMobile ? () => navigate(ROUTES.FILTERS_FOR_LIST) : undefined}
        onKeyDown={
          isMobile
            ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  navigate(ROUTES.FILTERS_FOR_LIST);
                }
              }
            : undefined
        }
      >
        <FilterHeaderHash className={styles.activeFiltersIcon} />
        <div className={styles.activeFiltersList}>
          {labels.map((label, index) => (
            <span key={`${label}_${index}`} className={styles.activeFiltersItem}>
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderMobileSearchHeader = () => {
    const filtersCount = activeFilterLabels.filter((label) => label !== t("all")).length;

    return (
      <div className={styles.mobileSearchHeader}>
        <h2>{title || t("search")}</h2>
        <button type="button" className={styles.mobileSearchFilterButton} onClick={() => navigate(ROUTES.FILTERS_FOR_LIST)}>
          {filtersCount > 0 && <span>{filtersCount}</span>}
          <FilterIcon isMobile />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.sectionWrapper}>
      <div className={styles.headerWrapper}>
        {enableViewSwitcher && isMobile ? (
          renderMobileSearchHeader()
        ) : enableViewSwitcher ? (
          renderSearchFilters()
        ) : (
          <Heading title={title} renderFilters={true} isFavoritePage={isFavoritePage} />
        )}
        {enableViewSwitcher && !isMobile && <ViewSwitcher activeView={activeView} onChange={setViewModeHandler} />}
      </div>
      {isFetching && !data.length ? (
        <div className={styles.loaderWrapper}>
          <DotsLoader />
        </div>
      ) : !data.length ? (
        emptyPageContent()
      ) : (
        <div className={styles.contentWrapper}>
          <div
            className={`${styles.cardListWrapper} ${isFavoritePage && viewMode !== "grid" ? styles.threeColumns : ""} ${
              viewMode === "grid" ? styles.gridCardListWrapper : ""
            } ${
              isFavoritePage && viewMode === "grid" ? styles.favoriteGridCardListWrapper : ""
            } ${
              isFavoritePage && viewMode !== "grid" ? styles.favoriteListCardListWrapper : ""
            }`}
          >
            {data.map((item: Movie, index: number) => {
              const cardProps = {
                item,
                isMobile,
                likeHandler,
                isFav: Boolean(favoritesList?.includes(item.id)),
                liked: Boolean(likesList?.includes(item.id)),
                favouritesHandler,
                updateVoteLocal: updateVoteCount,
              };

              return viewMode === "grid" ? (
                <VerticalMovieCard key={`${item.id}_${index}`} {...cardProps} />
              ) : (
                <MovieCard key={`${item.id}_${index}`} {...cardProps} />
              );
            })}
          </div>
          {!isLastData && (
            <div className={styles.loaderWrapper} ref={paginationAnchorRef}>
              <DotsLoader />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoviesList;
